import { HubConnectionBuilder, HubConnection, HubConnectionState } from "@microsoft/signalr";

export interface NotificationSubscription {
    methodName: string;
    handler: (...args: any[]) => void;
}

export class Notifications {
    private static readonly _activeConnections: Map<string, Notifications> = new Map();

    private _connection: HubConnection | null = null;
    private _hubUrl: string;

    private constructor(hubUrl: string, subscriptions: NotificationSubscription[]) {
        this._hubUrl = hubUrl;
        this.establishConnection(hubUrl, subscriptions);
    }

    private establishConnection(hubUrl: string, subscriptions: NotificationSubscription[]): void {
        const connection = new HubConnectionBuilder()
            .withUrl(hubUrl)
            .withAutomaticReconnect()
            .build();

        this._connection = connection;
        this.bindSubscriptions(connection, subscriptions);
        this.bindConnectionLifecycle(connection);

        connection.start()
            .then(() => {
                this.onConnected(connection);
            })
            .catch((error: Error) => {
                console.error(error.message);
                Notifications._activeConnections.delete(hubUrl);
            });
    }

    private bindSubscriptions(connection: HubConnection, subscriptions: NotificationSubscription[]): void {
        for (const subscription of subscriptions) {
            connection.off(subscription.methodName);
            connection.on(subscription.methodName, (...args: unknown[]) => {
                subscription.handler(...args);
            });
        }
    }

    private bindConnectionLifecycle(connection: HubConnection): void {
        connection.onclose((error?: Error) => {
            this.onConnectionError(error);
        });
    }

    public addSubscriptions(subscriptions: NotificationSubscription[]): void {
        if (!this._connection) {
            console.error("Cannot add subscriptions — connection is null");
            return;
        }
        this.bindSubscriptions(this._connection, subscriptions);
    }

    private onConnected(_connection: HubConnection): void {
        console.log("connection started");
    }

    private onConnectionError(error?: Error): void {
        if (error?.message) {
            console.error(error.message);
        }
    }

    public async stopAsync(): Promise<void> {
        if (this._connection && this._connection.state !== HubConnectionState.Disconnected) {
            await this._connection.stop();
        }
        this._connection = null;
        Notifications._activeConnections.delete(this._hubUrl);
    }

    public static create(url: string, subscriptions: NotificationSubscription[]): Notifications {
        const existing = Notifications._activeConnections.get(url);
        if (existing) {
            existing.addSubscriptions(subscriptions);
            return existing;
        }

        const instance = new Notifications(url, subscriptions);
        Notifications._activeConnections.set(url, instance);
        return instance;
    }
}
